<template>
  <div v-if="form == null" style="padding: 20px;">
    Chargement...
  </div>

  <div v-else style="padding: 20px;">
    <div class="row">
      <div class="col-md-6">
        <Preferences />
      </div>

      <div class="col-md-6">
        <PreparationTime />
      </div>
    </div>

    <!-- Exclusions d'aliments -->
    <div class="card" style="margin-top: 20px;">
      <div class="form-title">Ce que je ne peux / veux pas manger</div>

      <div v-if="nbExclusions >= nbExclusionsMax" style="color: red; font-weight: bold; margin-left: 10px;">
        Vous avez atteint la limite de {{ nbExclusionsMax }} aliments exclus maximum
      </div>

      <div class="excluor">
        <ExcluorButton v-model="form.exclude_eggs" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_eggs"
          @change="onExclusionChange">
          Œufs
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_arachid"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_arachid" @change="onExclusionChange">
          Arachides
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_cow_milk"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_cow_milk" @change="onExclusionChange">
          Lait de vache
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_nuts" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_nuts"
          @change="onExclusionChange">
          Fruits à coque
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_sea_food"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_sea_food" @change="onExclusionChange">
          Poissons et fruits de mer
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_soy" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_soy"
          @change="onExclusionChange">
          Soja
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_mustard"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_mustard" @change="onExclusionChange">
          Moutarde
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_pork" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_pork"
          @change="onExclusionChange">
          Porc
        </ExcluorButton>

        <!-- ALIMENTS EXCLUS DYNAMIQUES -->
        <ExcluorButton v-for="aliment in form.excluded_aliments" :key="aliment.id" :value="true"
          @input="removeExcludedAliment(aliment)"
          :disabled="nbExclusions >= nbExclusionsMax && !isAlimentExcluded(aliment.id)" @change="onExclusionChange">
          {{ aliment.name }}
        </ExcluorButton>
      </div>

      <div style="margin: 10px;">
        <label class="form-label">Exclure d'autres aliments</label>
        <input class="form-input" placeholder="Rechercher un aliment en particulier..."
          @input="searchAliment($event.target.value)" />
        <p style="margin-top : 5px; margin-bottom : 0">
          Mes Meilleurs Menus ne sont pas adaptés pour une alimentation végétarienne sans œufs et/ou sans produits
          laitiers ou pour une alimentation végane.
        </p>
      </div>

      <div v-if="alimentsToExclude">
        <div style="display: flex; flex-wrap: wrap;">
          <div v-for="aliment in alimentsToExclude" :key="aliment.id" class="excluor-button"
            @click="excludeAliment(aliment)" :class="{ disabled: nbExclusions >= nbExclusionsMax }">
            {{ aliment.name }}
          </div>
        </div>
      </div>
    </div>

    <!-- PROFILS DU FOYER -->
    <div class="row" style="margin-top: 20px;">
      <div class="col-md-2" style="flex: 1">
        <ConsumerProfil v-for="(consumer, index) in consumers" :key="consumer.id" :value="consumer"
          :firstPerson="index === 0" @deleted="removeConsumerFromList" @edit="openEditConsumerModal"
          style="margin-bottom: 10px" />
      </div>

      <div class="col-md-2" style="flex: 1; display: flex; align-items: center; justify-content: center;">
        <button class="button" @click="openAddConsumerModal">
          Ajouter quelqu'un d'autre dans mon foyer
        </button>
      </div>
    </div>

    <!-- MODAL AJOUT / ÉDITION -->
    <EditProfilModal v-if="showModal" :profil="currentConsumer" :firstPerson="false" @close="closeModal"
      @update="saveConsumer" />
  </div>
</template>

<script>
import { userState } from "@/utils/store";
import ExcluorButton from "@/components/ExcluorButton.vue";
import ConsumerProfil from "@/components/ConsumerProfil.vue";
import Preferences from "@/components/Preferences.vue";
import PreparationTime from "@/components/PreparationTime.vue";
import EditProfilModal from "@/components/EditProfilModal.vue";
import { api } from "@/api";

export default {
  name: "MesPreferences",
  components: {
    ExcluorButton,
    ConsumerProfil,
    Preferences,
    PreparationTime,
    EditProfilModal,
  },

  data() {
    return {
      form: null,
      aliments: null,
      nbExclusionsMax: 20,

      showModal: false,
      currentConsumer: null,
    };
  },

  computed: {
    preferences() {
      return userState.me.consumer_account;
    },

    consumers() {
      return this.preferences.consumers;
    },

    alimentsToExclude() {
      if (!this.form || !this.aliments) return [];
      return this.aliments.filter(
        (aliment) =>
          !this.form.excluded_aliments.find((a) => a.id === aliment.id)
      );
    },

    nbExclusions() {
      if (!this.form) return 0;
      return (
        this.form.excluded_aliments.length +
        (this.form.exclude_eggs ? 1 : 0) +
        (this.form.exclude_arachid ? 1 : 0) +
        (this.form.exclude_cow_milk ? 1 : 0) +
        (this.form.exclude_nuts ? 1 : 0) +
        (this.form.exclude_sea_food ? 1 : 0) +
        (this.form.exclude_soy ? 1 : 0) +
        (this.form.exclude_mustard ? 1 : 0) +
        (this.form.exclude_pork ? 1 : 0)
      );
    },
  },

  mounted() {
    this.reset();
  },

  methods: {
    reset() {
      this.form = {
        excluded_aliments: this.preferences.excluded_aliments,
        exclude_eggs: this.preferences.exclude_eggs,
        exclude_arachid: this.preferences.exclude_arachid,
        exclude_cow_milk: this.preferences.exclude_cow_milk,
        exclude_nuts: this.preferences.exclude_nuts,
        exclude_sea_food: this.preferences.exclude_sea_food,
        exclude_soy: this.preferences.exclude_soy,
        exclude_mustard: this.preferences.exclude_mustard,
        exclude_pork: this.preferences.exclude_pork,
      };
    },

    searchAliment(q) {
      if (q.length < 3) {
        this.aliments = [];
        return;
      }
      api.get(`/aliments?q=${q}`).then((response) => {
        this.aliments = response.data;
      });
    },

    onExclusionChange() {
      userState.savePreferences({
        excluded_aliments: this.form.excluded_aliments,
        exclude_eggs: this.form.exclude_eggs,
        exclude_arachid: this.form.exclude_arachid,
        exclude_cow_milk: this.form.exclude_cow_milk,
        exclude_nuts: this.form.exclude_nuts,
        exclude_sea_food: this.form.exclude_sea_food,
        exclude_soy: this.form.exclude_soy,
        exclude_mustard: this.form.exclude_mustard,
        exclude_pork: this.form.exclude_pork,
      });
    },

    excludeAliment(aliment) {
      this.form.excluded_aliments.push(aliment);
      this.onExclusionChange();
    },

    removeExcludedAliment(aliment) {
      this.form.excluded_aliments = this.form.excluded_aliments.filter(
        (a) => a.id !== aliment.id
      );
      this.onExclusionChange();
    },

    isAlimentExcluded(id) {
      return this.form.excluded_aliments.find((a) => a.id === id);
    },

    /* ----------------------------- FOYER ----------------------------- */

    openAddConsumerModal() {
      this.currentConsumer = {
        name: "",
        year_of_birth: "",
        gender: "male",
        want_to_loose_weight: false,
        height: "",
        weight: "",
        high_intensity_activity: 0,
        medium_intensity_activity: 0,
        low_intensity_activity: 0,
      };

      this.showModal = true;
    },

    openEditConsumerModal(consumer) {
      this.currentConsumer = { ...consumer };
      this.showModal = true;
    },

    saveConsumer(updatedConsumer) {
      // Si ID existe → update
      if (updatedConsumer.id) {
        const index = this.preferences.consumers.findIndex(
          (c) => c.id === updatedConsumer.id
        );
        this.preferences.consumers.splice(index, 1, updatedConsumer);
      } else {
        // Sinon → nouveau foyer
        this.preferences.consumers.push(updatedConsumer);
      }

      this.showModal = false;
    },

    closeModal() {
      this.showModal = false;
    },

    removeConsumerFromList(id) {
      this.preferences.consumers = this.preferences.consumers.filter(
        (c) => c.id !== id
      );
    },
  },
};
</script>

<style scoped>
.aliment-to-exclude {
  padding: 10px;
  margin: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
}
</style>
